import React from "react";
import Marquee from "react-fast-marquee";
import { Star, Phone, ArrowRight } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Container, Button, SectionHeading, Eyebrow } from "@/components/primitives";
import { Reveal } from "@/components/Reveal";
import { useBooking } from "@/lib/booking";
import { business, trustBadges, reviews, faqs } from "@/data/site";

export const TrustMarquee = () => (
  <div className="border-y border-[var(--border)] bg-[var(--surface)] py-6">
    <Marquee gradient={false} speed={38} pauseOnHover>
      {trustBadges.concat(trustBadges).map((b, i) => (
        <span key={i} className="mx-8 flex items-center gap-4 font-heading text-xl font-light italic tracking-tight text-[var(--muted-fg)]">
          {b}
          <span className="h-1 w-1 rounded-full bg-[var(--primary)]/40" />
        </span>
      ))}
    </Marquee>
  </div>
);

export const CTASection = ({
  eyebrow = "Book your visit",
  title = "Ready for a calmer, brighter dental experience?",
  intro = "Same-week appointments, transparent pricing and gentle care — right here in Hell's Kitchen.",
}) => {
  const { openBooking } = useBooking();
  return (
    <section className="py-24 sm:py-32">
      <Container>
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-[var(--border)] bg-[var(--primary)] px-8 py-16 text-center sm:px-16 sm:py-24">
            <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-black/10 blur-3xl" />
            <span className="relative font-mono text-xs uppercase tracking-[0.28em] text-[var(--on-primary)]/80">{eyebrow}</span>
            <h2 className="relative mx-auto mt-5 max-w-3xl font-heading text-4xl font-light leading-[1.05] tracking-tight text-[var(--on-primary)] sm:text-6xl text-balance">
              {title}
            </h2>
            <p className="relative mx-auto mt-6 max-w-xl text-base font-light text-[var(--on-primary)]/85">{intro}</p>
            <div className="relative mt-10 flex flex-col justify-center gap-3 sm:flex-row">
              <Button size="lg" variant="light" onClick={() => openBooking()} data-testid="cta-book-btn">
                Book Appointment
              </Button>
              <a
                href={business.phoneHref}
                data-testid="cta-call-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-8 py-4 text-sm font-medium text-[var(--on-primary)] transition-colors hover:bg-white/10"
              >
                <Phone size={16} /> Call / Text {business.phoneDisplay}
              </a>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
};

export const ReviewsSection = () => (
  <section className="bg-[var(--surface)] py-24 sm:py-32">
    <Container>
      <SectionHeading eyebrow="Patient stories" title="Trusted by New Yorkers who don't have time for the ordinary." />
      <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {reviews.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.08}>
            <figure className="flex h-full flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-7">
              <div className="flex gap-0.5 text-[var(--primary)]">
                {Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={15} fill="currentColor" />)}
              </div>
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-[var(--fg)]/85">"{r.text}"</blockquote>
              <figcaption className="mt-6 border-t border-[var(--border)] pt-4">
                <span className="block text-sm font-medium text-[var(--fg)]">{r.name}</span>
                <span className="block font-mono text-xs uppercase tracking-[0.15em] text-[var(--muted-fg)]">{r.area}</span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

export const FaqSection = ({ items = faqs, limit, eyebrow = "Good to know", title = "Questions, answered.", showAllLink = false }) => {
  const list = limit ? items.slice(0, limit) : items;
  return (
    <section className="py-24 sm:py-32">
      <Container>
        <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <SectionHeading eyebrow={eyebrow} title={title} />
            {showAllLink && (
              <Button to="/faq" variant="outline" className="mt-8" data-testid="faq-view-all">
                All FAQs & Emergency Care <ArrowRight size={16} />
              </Button>
            )}
          </div>
          <Accordion type="single" collapsible className="w-full">
            {list.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[var(--border)]" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="py-5 text-left font-heading text-xl font-normal tracking-tight text-[var(--fg)] hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-[var(--muted-fg)]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Container>
    </section>
  );
};

export const PageHero = ({ eyebrow, title, intro, image, imageAlt }) => (
  <section className="relative overflow-hidden pt-36 pb-16 sm:pt-44 sm:pb-24">
    <Container>
      <div className="grid items-end gap-10 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-5 font-heading text-5xl font-light leading-[0.98] tracking-tight text-[var(--fg)] sm:text-6xl md:text-7xl text-balance">
              {title}
            </h1>
          </Reveal>
          {intro && (
            <Reveal delay={0.16}>
              <p className="mt-7 max-w-xl text-base font-light leading-relaxed text-[var(--muted-fg)] sm:text-lg">{intro}</p>
            </Reveal>
          )}
        </div>
        {image && (
          <Reveal delay={0.2}>
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-[var(--border)]">
              <img src={image} alt={imageAlt || title} className="h-full w-full object-cover" loading="eager" />
            </div>
          </Reveal>
        )}
      </div>
    </Container>
  </section>
);
